import React from 'react';
import { injectIntl } from 'react-intl';
import { Grid, IconButton, LinearProgress, Typography } from '@mui/material';
import _ from 'lodash';
import { formatNumber } from '../../../../../../../utils/formatNumber';
import ArrowForwardIosRoundedIcon from '@mui/icons-material/ArrowForwardIosRounded';
import { makeStyles } from 'tss-react/mui';
import './TopClientTable.scss';

const COLUMN_STYLES = [
  {
    width: 45
  },
  {
    width: 'calc((100% - 175px) / 7 * 3)'
  },
  {
    width: 'calc((100% - 175px) / 7 * 4)'
  },
  {
    width: 130
  }
];

const RowStyle = {
  height: 40
};

const UserRowStyle = {
  cursor: 'pointer'
};

const NameTypoStyle = {
  overflowX: 'hidden',
  whiteSpace: 'nowrap',
  textOverflow: 'ellipsis'
};

const ActionButtonStyle = {
  width: '100%'
};

const ActionButtonIconStyle = {
  fontSize: '16px'
};

const EntriesRegionStyle = {
  height: 200,
  overflow: 'hidden auto'
};

const useProgressStyles = makeStyles()((theme, { progressColor, progressBackgroundColor }) => ({
  root: {
    width: '100%',
    maxWidth: 150,
    margin: 'auto',
    height: 5,
    borderRadius: 15,
    backgroundColor: progressBackgroundColor
  },
  bar: {
    backgroundColor: progressColor
  }
}));

// Gold, silver and bronze for the podium; a soft blue for the rest.
const RANK_COLORS = [
  { background: 'linear-gradient(135deg, #ffd860, #f2a900)', color: '#fff' },
  { background: 'linear-gradient(135deg, #d9dee8, #9aa5b8)', color: '#fff' },
  { background: 'linear-gradient(135deg, #e8b48a, #c27a3e)', color: '#fff' }
];
const DEFAULT_RANK_COLOR = { background: '#eef3ff', color: '#3966f8' };

const RankBadge = ({ rank }) => {
  const { background, color } = RANK_COLORS[rank - 1] || DEFAULT_RANK_COLOR;
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: 24,
        height: 24,
        borderRadius: '50%',
        background,
        color,
        fontSize: 13,
        fontWeight: 700,
        lineHeight: 1
      }}
    >
      {rank}
    </span>
  );
};

const getProgress = (value, maxValue) => (!maxValue ? 0 : ((value || 0) / maxValue) * 100);

const TopClientTable = props => {
  const { intl, valueLabel, progressColor, progressBackgroundColor, items, onClickItem } = props;

  const formattedItems = items || [];
  const emptyItemsLength = 10 - formattedItems.length;
  const maxValue = _.get(formattedItems, '[0].value');

  const { classes } = useProgressStyles({ progressColor, progressBackgroundColor });

  return (
    <Grid container className="top-client-table">
      <Grid size={12}>
        <Grid container spacing={1}>
          <Grid size={12}>
            <Grid
              container
              className="top-client-table-row"
              style={RowStyle}
              sx={{
                alignItems: 'center'
              }}
            >
              <Grid style={COLUMN_STYLES[0]} />
              <Grid style={COLUMN_STYLES[1]}>
                <Typography align="center" color="textSecondary">
                  {intl.formatMessage({ id: 'Client' })}
                </Typography>
              </Grid>
              <Grid style={COLUMN_STYLES[2]} />
              <Grid style={COLUMN_STYLES[3]}>
                <Typography align="center" color="textSecondary">
                  {valueLabel}
                </Typography>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
      <Grid size={12} style={EntriesRegionStyle}>
        <Grid container spacing={1}>
          {formattedItems.map((item, index) => {
            return (
              <Grid key={item.userId} size={12} onClick={() => onClickItem(item)} style={UserRowStyle}>
                <Grid
                  container
                  className="top-client-table-row"
                  style={RowStyle}
                  sx={{
                    alignItems: 'center'
                  }}
                >
                  <Grid style={COLUMN_STYLES[0]}>
                    <Typography align="center" component="div">
                      <RankBadge rank={index + 1} />
                    </Typography>
                  </Grid>
                  <Grid style={COLUMN_STYLES[1]}>
                    <Typography align="center" style={NameTypoStyle}>
                      {item.userName}
                    </Typography>
                  </Grid>
                  <Grid style={COLUMN_STYLES[2]}>
                    <LinearProgress
                      variant="determinate"
                      value={getProgress(item.value, maxValue)}
                      classes={{
                        root: classes.root,
                        bar: classes.bar
                      }}
                    />
                  </Grid>
                  <Grid style={COLUMN_STYLES[3]}>
                    <Grid
                      container
                      spacing={1}
                      sx={{
                        justifyContent: 'center'
                      }}
                    >
                      <Grid>
                        <Typography align="center">{formatNumber(item.value, intl)}</Typography>
                      </Grid>
                      <Grid>
                        <IconButton color="primary" size="small" style={ActionButtonStyle}>
                          <ArrowForwardIosRoundedIcon style={ActionButtonIconStyle} />
                        </IconButton>
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            );
          })}
          {new Array(emptyItemsLength).fill(0).map((_, index) => (
            <Grid key={index} size={12}>
              <Grid
                container
                className="top-client-table-row"
                style={RowStyle}
                sx={{
                  alignItems: 'center'
                }}
              >
                <Grid style={COLUMN_STYLES[0]}>
                  <Typography align="center" component="div">
                    <RankBadge rank={formattedItems.length + index + 1} />
                  </Typography>
                </Grid>
                <Grid style={COLUMN_STYLES[1]}>
                  <Typography align="center">-</Typography>
                </Grid>
                <Grid style={COLUMN_STYLES[2]}>
                  <LinearProgress
                    variant="determinate"
                    value={0}
                    classes={{
                      root: classes.root,
                      bar: classes.bar
                    }}
                  />
                </Grid>
                <Grid style={COLUMN_STYLES[3]}>
                  <Typography align="center">-</Typography>
                </Grid>
              </Grid>
            </Grid>
          ))}
        </Grid>
      </Grid>
    </Grid>
  );
};

export default injectIntl(TopClientTable);
